import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip, 
	RCCInput,
	RCCServicesApi,
	RCCHookToast,
	RCCHierarchicalTree,
	RCCAlert,
	removeNode,
	changeNodeAtPath,
	getFlatDataFromTree,
	getNodeAtPath
} from "react-common-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import { IndicatorLevel, IRefIndicatorModal } from "src/components/ModalIndicator/interface";
import IndicatorModal from "src/components/ModalIndicator";
import { standardErrorMessages } from "src/utils/errors";
import LovSectors from "src/components/Lov/Sectors";
import LovClassifications from "src/components/Lov/Classifications";
import LovUsers from "src/components/Lov/Users";
import { css } from "styled-components";
import Loader from "src/components/Loader";
import Select from "src/components/Select";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { useAuth } from "src/context/Auth/context";
import { IIndicatorHierarchyItem, IIndicatorListFilters, IRefIndicatorGrouper } from "./interface";
import IndicatorGrouper from "./components/grouper";

const IndicatorsHierarchy: React.FC = () => { 
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const [selectedLevel,SetselectedLevel] = useState<IndicatorLevel>(IndicatorLevel.STRATEGIC);
	const refModal = useRef<IRefIndicatorModal>(null);
	const refStrategic = useRef<IRefIndicatorGrouper>(null);
	const refTatic = useRef<IRefIndicatorGrouper>(null);
	const refOperational = useRef<IRefIndicatorGrouper>(null);
	const { checkIfHasSpecificPermission } = useAuth();

	const [itens,setItens] = useState<Array<any>>([]);
	const [oldItens,setOldItens] = useState<Array<any>>([]);

	const hasPermissionToCreateIndicator = checkIfHasSpecificPermission("indicators/panel", PermissionEnum.CREATE_UPDATE);

	const groupers = [
		{id:IndicatorLevel.STRATEGIC,ref:refStrategic},
		{id:IndicatorLevel.TATIC,ref:refTatic},
		{id:IndicatorLevel.OPERATIONAL,ref:refOperational}
	];

	const getNodeKeyFunction =({treeIndex}:any) => treeIndex;

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IIndicatorListFilters>({filter:"",situation:"T"});
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters,
		control:controlFilters, 
		reset: resetFilters, 
		watch: watchFilters 
	} = useForm();

	const areUsingFilter = ():boolean => !!filters.classification || !!filters.filter || !!filters.responsible || !!filters.sector || filters.situation !== "T";

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as IIndicatorListFilters;
			newState.filter = data.filter;
			newState.situation = data.situation.value;

			if(data.classification)
				newState.classification = typeof data.classification === "object" ? data.classification?.value : data.classification;
			if(data.sector)
				newState.sector = typeof data.sector === "object" ? data.sector?.value : data.sector;
			if(data.responsible)
				newState.responsible = typeof data.responsible === "object" ? data.responsible?.value : data.responsible;

			return newState;
		});
	};

	const { errorFetching, loading } = useEffectFetchData<any[]> (
		"Indicators/ListHierarchy", 
		undefined,
		({ data }) => {
			setItens(data);
			setOldItens(data);
		}
	);

	const handleTreeMove = async(data:any) => {
		try {
			const {node,nextParentNode,prevPath,treeData} = data;
			let alteredList;
			if(nextParentNode){
				prevPath.pop();
			
				alteredList = nextParentNode?.children.map((item:any,index:number) => ({ 
					...item,
					iqe001Ordem : index + 1,
					iqe001Iqe001Id:nextParentNode?.iqe001Id
				}));

				const previusParent = getNodeAtPath({treeData:[...oldItens],getNodeKey:getNodeKeyFunction,path:prevPath});

				if(previusParent?.node?.iqe001Id !== nextParentNode?.iqe001Id){
					const oldNodeChildrens = previusParent?.node?.children
						.filter((x:any) => x.iqe001Id !== node.iqe001Id)
						.map((item:any,index:number) => ({ 
							...item,
							iqe001Ordem : index + 1
						}));
					alteredList = [...oldNodeChildrens,...alteredList];
				}
			
			}else{
				alteredList = treeData.map((item:any,index:number) => ({ 
					...item,
					iqe001Ordem : index + 1,
					iqe001Iqe001Id:null
				}));
			}
			await RCCServicesApi.postRequest({
				method: "Indicators/UpdateHierarchy",
				body:alteredList
			});

			setOldItens(treeData);
		} catch (error) {
			standardErrorMessages(error, translate("Erro ao atualizar hierarquia"), addToast);
			setItens(oldItens);
		}
	};

	const canDrop = (data:any):boolean => {
		// estrategico
		if(!data.prevParent && !data.nextParent){
			return true;
		}
		// tatico
		if(data.nextParent && 
			data.nextParent.iqc001Nivel === IndicatorLevel.STRATEGIC && 
			data.node.iqc001Nivel === IndicatorLevel.TATIC){
			return true;
		}
		// operacional
		if(data.nextParent && 
			data.nextParent.iqc001Nivel === IndicatorLevel.TATIC && 
			data.node.iqc001Nivel === IndicatorLevel.OPERATIONAL){
			return true;
		}
		return false;
	};

	const insertOnTree = async (node?:any, path?:Array<string | number>) => {
		let selectedList = [] as Array<IIndicatorHierarchyItem>;
		let cleanFunction;
		let levelList;

		const insertRequest = async(list:Array<IIndicatorHierarchyItem>) => {
			try {
				const dataController = await RCCServicesApi.postRequest({
					method: "Indicators/InsertHierarchy",
					body:list
				});
				return dataController.data as Array<IIndicatorHierarchyItem>;
	
			} catch (error) {
				standardErrorMessages(error, translate("Erro ao inserir itens na hierarquia"), addToast);
				return [];
			}
		};
		
		switch (node?.iqc001Nivel) 
		{
		case IndicatorLevel.STRATEGIC:
			selectedList = refTatic.current?.getSelectedIndicators()||[];
			cleanFunction = refTatic.current?.cleanSelectedIndicators;
			levelList=translate("tático");
			break;
		case IndicatorLevel.TATIC:
			selectedList = refOperational.current?.getSelectedIndicators()||[];
			cleanFunction = refOperational.current?.cleanSelectedIndicators;
			levelList=translate("operacional");
			break;
		default:	
			selectedList = refStrategic.current?.getSelectedIndicators()||[];
			cleanFunction = refStrategic.current?.cleanSelectedIndicators;
			levelList=translate("estratégico");
			break;
		}
		
		if(selectedList.length <= 0){
			addToast({
				color: "yellow",
				description: translate("Selecione indicadores de nivel {{level}} para inserir na hierarquia",{level:levelList}),
				title: translate("Aviso")
			});
			return;
		}

		let newTreeData;
		if(!node){
			const arrayLastPosition = itens.length > 0 ? itens[itens.length-1].iqe001Ordem : 0;

			const ordenedList = selectedList.map((item:any,index:number) => ({ 
				...item,
				iqe001Id : 0,
				iqe001Iqe001Id : null,
				iqe001Ordem : index + arrayLastPosition + 1
			}));

			const insertedList = await insertRequest(ordenedList);
			if(insertedList.length <= 0)
				return;

			newTreeData = [...itens,...insertedList];

		}else{
			const modifiedNode = {...node};
			const arrayLastPosition = modifiedNode?.children.length > 0 ? modifiedNode?.children[modifiedNode?.children.length-1].iqe001Ordem : 0;
			const ordenedList = selectedList.map((item:any,index:number) => ({ 
				...item,
				iqe001Id : 0,
				iqe001Iqe001Id : node.iqe001Id,
				iqe001Ordem : index + arrayLastPosition + 1
			}));
			const insertedList = await insertRequest(ordenedList);
			if(insertedList.length <= 0)
				return;
				
			modifiedNode.children = [...modifiedNode?.children,...insertedList];
			modifiedNode.expanded = true;
			newTreeData = changeNodeAtPath({treeData:[...itens],path,getNodeKey:getNodeKeyFunction,newNode:modifiedNode});
		}
				
		setItens(newTreeData);

		if(cleanFunction)
			cleanFunction();	
	};

	const isChildrenLevel = (parent:IndicatorLevel,children:IndicatorLevel):boolean => (
		parent === IndicatorLevel.STRATEGIC && ( children === IndicatorLevel.STRATEGIC || children === IndicatorLevel.TATIC))
		|| (parent === IndicatorLevel.TATIC && ( children === IndicatorLevel.OPERATIONAL));

	const renderGroupers = () => 
		<>
			{groupers.map((grouper) => 
				<IndicatorGrouper 
					key={`${grouper.id}-grouper-indicators`}
					filters={filters}
					level={grouper.id}
					disabled={!isChildrenLevel(selectedLevel,grouper.id)}
					insertStrategicLevel={grouper.id === IndicatorLevel.STRATEGIC && insertOnTree}
					requestMethod="Indicators/ListIndicatorsHierarchy"
					filterHierarchy
					ref={grouper.ref}
				/>
			)}
		</>
	;

	const renderHierarchy = () => (
		itens.length > 0 ? 
			<RCCHierarchicalTree
				data={itens}
				onMoveNode={handleTreeMove}
				onChange={(treeData) => setItens(treeData)}
				canDrop={canDrop}
				maxDepth={3}
				canDrag={checkIfHasSpecificPermission("indicators/hierarchy", PermissionEnum.CREATE_UPDATE)}
				styleInline={css` .rst__rowContents{ padding:0; }`}
				customNode={({
					node,
					path
				}) => (
					<RCCGrid container 
						styleInline={
							css`
								padding:0 10px;
								height:100%;
								${selectedLevel!==node.iqc001Nivel && "font-weight:normal;"}
								:hover{box-shadow:2px 2px 2px 2px var(--border-color);}
							`
						}
						onClick={() => SetselectedLevel(node.iqc001Nivel)}
						justifyContent="space-between">
						<RCCGrid container item xs={9} styleInline="overflow:hidden;" alignItems="center">
							<span 
								style={{
									whiteSpace:"normal",
									display:"-webkit-box",
									maxWidth:"fit-content",
									WebkitLineClamp:2,
									WebkitBoxOrient:"vertical",
									fontSize: "0.83rem"
								}}>
								{node.iqc001Descricao}
							</span>
						</RCCGrid>
						<RCCGrid container item justifyContent="flex-end" xs={3}>
							{selectedLevel===node.iqc001Nivel && node.iqc001Nivel !== IndicatorLevel.OPERATIONAL &&
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<RCCTooltip
									text={translate("Inserir na hierarquia")}
									color="green"
									position="leftEnd"
								>
									<RCCButton
										icon={{name: "arrowBack"}}
										color="green"
										layout="isRounded"
										size="xs"
										onClick={() => insertOnTree(node,path)}
									/>
								</RCCTooltip>
							</CheckPermission>
							}
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
								<RCCTooltip
									text={translate("Retirar da hierarquia")}
									color="red"
									position="leftEnd"
								>
									<RCCButton
										icon={{name: "trash"}}
										color="red"
										layout="isRounded"
										size="xs"
										onClick={() => removeItem(path)}
									/>
								</RCCTooltip>
							</CheckPermission>
						</RCCGrid>
				
					</RCCGrid>
				)}
			/>
			:
			<RCCAlert 
				title={translate("Atenção")}
				description={translate("Não foi encontrado indicadores para esta hierarquia")}
				color="blue"
			/>
	);

	const removeItem = async(path:Array<string|number>) => {

		try {
			const data = removeNode({treeData:[...itens],path,getNodeKey:getNodeKeyFunction});
			
			await RCCServicesApi.deleteRequest({
				method: "Indicators/DeleteHierarchy",
				urlParameters:{id:data.node.iqe001Id}
			});

			const flatArray = getFlatDataFromTree({treeData:[data.node],getNodeKey:getNodeKeyFunction,ignoreCollapsed:false});
			const StrategicArray = flatArray.filter((x:any) => x.node.iqc001Nivel===IndicatorLevel.STRATEGIC).map((y:any) => y.node);
			const taticArray = flatArray.filter((x:any) => x.node.iqc001Nivel===IndicatorLevel.TATIC).map((y:any) => y.node);
			const operationalArray = flatArray.filter((x:any) => x.node.iqc001Nivel===IndicatorLevel.OPERATIONAL).map((y:any) => y.node);
			
			if(StrategicArray.length > 0)
				refStrategic.current?.addIndicators(StrategicArray);
			if(taticArray.length > 0)
				refTatic.current?.addIndicators(taticArray);
			if(operationalArray.length > 0)
				refOperational.current?.addIndicators(operationalArray);
		
			setItens(data?.treeData||[]);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao excluir item da hierarquia"), addToast);
		}
		
	};

	const forceGrouperRequery = (newIndicator :any) => {
		const grouper = groupers.find((x) => x.id === newIndicator.indicator.iqc001Nivel);
		if(grouper)
			grouper.ref.current?.forceRequery();
	};

	return (
		<RCCGrid container item
			spacing={5}
			direction="row-reverse"
		>
			<RCCGrid container item
				direction="column"
				sm={12}
				md={6}
			>
				<PageTitle
					title={translate("Indicadores")}
					icon={{
						name: "chartBar"
					}}
				>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || areUsingFilter() ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || areUsingFilter() ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					{hasPermissionToCreateIndicator && (
						<RCCTooltip
							text={translate("Novo indicador")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => { refModal?.current?.actionButtonAdd((newIndicator) => forceGrouperRequery(newIndicator));}}
								size="md"
							/>
						</RCCTooltip>
					)}
				</PageTitle>
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						filter: "",
						sector:{value:"",label:""},
						responsible:{value:"",label:""},
						classification:{value:"",label:""},
						situation:{value: "T", label: translate("Todos")}
					})}
					showFilters={showFilters}
				>	
					<RCCGrid container item>
						<RCCGrid container item
							direction="row"
							spacing={1}
						>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<LovSectors
									control={controlFilters}
									name="sector"
									label={translate("Setor")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<LovClassifications
									control={controlFilters}
									name="classification"
									label={translate("Classificação")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
							>
								<LovUsers
									control={controlFilters}
									name="responsible"
									label={translate("Responsável")}
									isClearable
								/>
							</RCCGrid>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<Select 
									control={controlFilters}
									name="situation" 
									options={[
										{value: "A", label: translate("Ativo")}, 
										{value: "I", label: translate("Inativo")},
										{value: "T", label: translate("Todos")}
									]} 
									label={translate("Situação")}
									defaultValue={{value: "T", label: translate("Todos")}}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
				<RCCGrid container
					styleInline="margin-top:0.5rem;">
					{renderGroupers()}
				</RCCGrid>
			</RCCGrid>
			<RCCGrid container item
				direction="column"
				sm={12}
				md={6}
			>
				<PageTitle
					title={translate("Hierarquia")}
					icon={{
						name: "hierarchy"
					}}
				/>

				{errorFetching ? (
					<RCCAlert 
						color="red"
						title={translate("Atenção")}
						description={translate("Erro ao carregar dados")}
					/>
				): 
					<>
						{loading ? 
							<Loader /> : 
							<>
								{renderHierarchy()}
							</>
						}
					</>
				}
			</RCCGrid>
			<IndicatorModal ref={refModal} /> 
		</RCCGrid>
	);
};

export default IndicatorsHierarchy;
